import { useNavigate, Link } from "react-router-dom";
import { Trophy, Users, Fish, ShieldCheck, Anchor, Languages, ExternalLink } from "lucide-react";
import PageMeta from "./PageMeta";

const benefits = [
  {
    icon: Trophy,
    heading: "Shore & boat competitions",
    body: "A full season of fixtures — Colin Rule series, Easter, Conger, Float & Spin, Bream and more — plus optional club boat trips.",
  },
  {
    icon: Fish,
    heading: "Junior section",
    body: "Junior membership is just £1 a year. Young anglers compete for their own trophies and are mentored by experienced club members.",
  },
  {
    icon: Users,
    heading: "A welcoming community",
    body: "Anglers of all abilities are welcome, from complete beginners to seasoned specimen hunters. Monthly meetings are open to prospective members.",
  },
  {
    icon: ShieldCheck,
    heading: "Angling Trust affiliated",
    body: "RSAA is affiliated to the Angling Trust and the Cornish Federation of Sea Anglers, giving members access to wider angling representation and support.",
  },
  {
    icon: Anchor,
    heading: "Club records & recognition",
    body: "Submit your catches to the Fish Recorder and get in the running for club records, trophies, and Fish of the Month.",
  },
  {
    icon: Languages,
    heading: "Available in Kernowek",
    body: "The whole site can be viewed in Kernowek (Cornish language) as well as English — proud to serve Cornwall's angling and language community.",
  },
];

const faqs = [
  {
    q: "Do I need a boat to join?",
    a: "No. Most RSAA competitions are shore-based. Club boat trips are organised throughout the season and are optional.",
  },
  {
    q: "Is RSAA suitable for beginners?",
    a: "Yes. We welcome sea anglers of all abilities, from complete beginners to experienced specimen hunters. Come along to a meeting to meet the club before you join.",
  },
  {
    q: "What does my membership include?",
    a: "Entry to shore and boat competitions, access to club records and trophies, junior section support, angler safety resources, and affiliation with the Angling Trust and Cornish Federation of Sea Anglers.",
  },
  {
    q: "How do I pay?",
    a: "By card or bank transfer (BACS) through the membership form, or in person at one of our monthly meetings.",
  },
  {
    q: "Can my child join?",
    a: "Yes. Junior membership is £1 a year and is open to junior anglers, with the application co-signed by a parent or guardian.",
  },
  {
    q: "When and where are club meetings?",
    a: "Monthly at St Rumons Social Club, Penryn Street, Redruth, TR15 2SP. New and prospective members are always welcome to attend.",
    mapAddress: "St Rumons Social Club, Penryn Street, Redruth, TR15 2SP",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
};

export default function MembershipPage() {
  const navigate = useNavigate();

  return (
    <div className="page-shell min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <PageMeta
        title="Join RSAA — Sea Angling Club Membership Cornwall | Redruth Sea Angling Association"
        description="Join Redruth Sea Angling Association, Cornwall's leading sea angling club since 1962. Adult membership £15, junior £1. Shore & boat competitions, junior section, club records, Angling Trust affiliated. Available in Kernowek."
        keywords="join sea angling club Cornwall, RSAA membership, sea fishing club membership, Redruth Sea Angling Association, junior angling membership, Cornwall angling club fees, how to join a sea angling club"
        url="https://www.redruthseaanglingassociation.co.uk/membership"
        image="https://www.redruthseaanglingassociation.co.uk/assets/scenic_background.png"
      />
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center gap-4">
          <button type="button" onClick={() => navigate(-1)}
            className="standalone-only group inline-flex items-center gap-1.5 rounded-full border border-cyan-400/20 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200 shadow-lg shadow-black/20 backdrop-blur transition hover:-translate-x-0.5 hover:border-cyan-300/40 hover:bg-cyan-400/10 hover:text-white">
            ← Back
          </button>
          <div>
            <h1 className="heading-glow text-2xl font-bold text-white sm:text-3xl">Membership</h1>
            <p className="mt-1 text-sm text-slate-400">Join the Redruth Sea Angling Association</p>
          </div>
        </div>

        <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">Membership</p>
        <h2 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">How to join the Redruth Sea Angling Association</h2>
        <p className="mt-4 max-w-2xl text-base leading-7 text-slate-300">
          Cornwall's leading sea angling club since 1962. Whatever your experience — shore or boat, beginner or specimen hunter — there's a place for you at RSAA.
        </p>

        {/* Why join */}
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {benefits.map(({ icon: Icon, heading, body }) => (
            <div key={heading} className="rounded-2xl border border-white/10 bg-white/5 p-5 panel-lift">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-400/10 border border-cyan-300/20">
                <Icon className="h-5 w-5 text-cyan-300" />
              </div>
              <h3 className="mt-3 text-base font-semibold text-white">{heading}</h3>
              <p className="mt-1.5 text-sm leading-6 text-slate-300">{body}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-2">
          <div className="rounded-[2rem] border border-cyan-300/20 bg-cyan-400/10 p-6 panel-lift">
            <p className="text-sm uppercase tracking-[0.25em] text-cyan-200/80">Adult annual subscription</p>
            <p className="mt-3 text-5xl font-bold text-white">£15</p>
            <p className="mt-4 text-sm leading-7 text-slate-200">
              Includes club membership, liability cover, and support for general administration.
            </p>
          </div>

          <div className="rounded-[2rem] border border-amber-300/20 bg-amber-400/10 p-6 panel-lift">
            <p className="text-sm uppercase tracking-[0.25em] text-amber-200/80">Junior annual subscription</p>
            <p className="mt-3 text-5xl font-bold text-white">£1</p>
            <p className="mt-4 text-sm leading-7 text-slate-200">
              Open to junior members. A parent or guardian must co-sign the membership application.
            </p>
          </div>

          <div className="rounded-[2rem] border border-white/10 bg-white/5 p-6 md:col-span-2 panel-lift">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-xl font-semibold">Ready to join Redruth Sea Angling Association?</h3>
                <p className="mt-2 text-sm leading-7 text-slate-300">
                  Adult membership is £15 · Junior membership is £1. Complete the form and pay via card or bank transfer. New members are also welcome to come along to a meeting and pay in person.
                </p>
              </div>
              <Link
                to="/contact"
                className="shrink-0 rounded-2xl bg-cyan-400 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:scale-[1.02]"
              >
                Join now →
              </Link>
            </div>
          </div>

          <div className="rounded-[2rem] border border-white/10 bg-white/5 p-6 md:col-span-2 panel-lift">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold">Not ready to commit? Come along first.</h3>
                <p className="mt-2 text-sm leading-7 text-slate-300">
                  Prospective members are welcome at our monthly meetings at St Rumons Social Club, Penryn Street, Redruth, TR15 2SP — meet the committee and members before you join.
                </p>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("St Rumons Social Club, Penryn Street, Redruth, TR15 2SP")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-300 transition hover:text-cyan-200"
                >
                  See on map <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <Link
                to="/upcoming-meeting"
                className="shrink-0 rounded-2xl border border-cyan-300/40 bg-cyan-400/10 px-5 py-3 text-sm font-semibold text-cyan-200 transition hover:bg-cyan-400/20"
              >
                Next meeting details →
              </Link>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-14">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">FAQ</p>
          <h2 className="mt-3 text-2xl font-bold tracking-tight text-white sm:text-3xl">Membership questions</h2>
          <div className="mt-6 space-y-4">
            {faqs.map(({ q, a, mapAddress }) => (
              <div key={q} className="rounded-2xl border border-white/10 bg-white/5 p-5 panel-lift">
                <h3 className="text-base font-semibold text-white">{q}</h3>
                <p className="mt-2 text-sm leading-7 text-slate-300">{a}</p>
                {mapAddress && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapAddress)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-300 transition hover:text-cyan-200"
                  >
                    See on map <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
